// Role definitions (mirrored from backend)
export const SYSTEM_ROLES = {
    SUPER_ADMIN: 'super_admin',
    TENANT_OWNER: 'tenant_owner',
    ADMIN: 'admin',
    MANAGER: 'manager',
    EMPLOYEE: 'employee',
    ACCOUNTANT: 'accountant',
    VENDOR: 'vendor',
    CLIENT: 'client',
} as const;

export type SystemRole = (typeof SYSTEM_ROLES)[keyof typeof SYSTEM_ROLES];

// Permission definitions (mirrored from backend)
export const PERMISSIONS = {
    // Dashboard
    DASHBOARD_VIEW: 'dashboard:view',

    // Users & Roles
    USERS_VIEW: 'users:view',
    USERS_CREATE: 'users:create',
    USERS_UPDATE: 'users:update',
    USERS_DELETE: 'users:delete',
    ROLES_MANAGE: 'roles:manage',

    // Settings
    SETTINGS_VIEW: 'settings:view',
    SETTINGS_UPDATE: 'settings:update',
    TENANT_SETTINGS: 'tenant:settings',

    // CRM
    LEADS_VIEW: 'leads:view',
    LEADS_CREATE: 'leads:create',
    LEADS_UPDATE: 'leads:update',
    LEADS_DELETE: 'leads:delete',
    LEADS_CONVERT: 'leads:convert',

    CLIENTS_VIEW: 'clients:view',
    CLIENTS_CREATE: 'clients:create',
    CLIENTS_UPDATE: 'clients:update',
    CLIENTS_DELETE: 'clients:delete',

    CONTACTS_VIEW: 'contacts:view',
    CONTACTS_CREATE: 'contacts:create',
    CONTACTS_UPDATE: 'contacts:update',
    CONTACTS_DELETE: 'contacts:delete',

    // Sales
    QUOTATIONS_VIEW: 'quotations:view',
    QUOTATIONS_CREATE: 'quotations:create',
    QUOTATIONS_UPDATE: 'quotations:update',
    QUOTATIONS_DELETE: 'quotations:delete',
    QUOTATIONS_APPROVE: 'quotations:approve',
    QUOTATIONS_SEND: 'quotations:send',
    QUOTATIONS_CONFIRM: 'quotations:confirm',

    SALES_ORDERS_VIEW: 'sales_orders:view',
    SALES_ORDERS_UPDATE: 'sales_orders:update',
    SALES_ORDERS_DELETE: 'sales_orders:delete',
    SALES_ORDERS_INVOICE: 'sales_orders:invoice',
    SALES_ORDERS_CANCEL: 'sales_orders:cancel',

    // Jobs
    JOBS_VIEW: 'jobs:view',
    JOBS_CREATE: 'jobs:create',
    JOBS_UPDATE: 'jobs:update',
    JOBS_DELETE: 'jobs:delete',
    JOBS_ASSIGN: 'jobs:assign',
    JOBS_STATUS: 'jobs:status',

    // Production
    PRODUCTION_VIEW: 'production:view',
    PRODUCTION_UPDATE: 'production:update',
    PRODUCTION_MANAGE: 'production:manage',

    // Finance
    INVOICES_VIEW: 'invoices:view',
    INVOICES_CREATE: 'invoices:create',
    INVOICES_UPDATE: 'invoices:update',
    INVOICES_DELETE: 'invoices:delete',
    INVOICES_SEND: 'invoices:send',

    PAYMENTS_VIEW: 'payments:view',
    PAYMENTS_CREATE: 'payments:create',
    PAYMENTS_UPDATE: 'payments:update',

    EXPENSES_VIEW: 'expenses:view',
    EXPENSES_CREATE: 'expenses:create',
    EXPENSES_UPDATE: 'expenses:update',
    EXPENSES_APPROVE: 'expenses:approve',

    // Purchases
    PURCHASES_VIEW: 'purchases:view',
    PURCHASES_CREATE: 'purchases:create',
    PURCHASES_UPDATE: 'purchases:update',
    PURCHASES_APPROVE: 'purchases:approve',

    // Vendors
    VENDORS_VIEW: 'vendors:view',
    VENDORS_CREATE: 'vendors:create',
    VENDORS_UPDATE: 'vendors:update',
    VENDORS_DELETE: 'vendors:delete',

    // Products
    PRODUCTS_VIEW: 'products:view',
    PRODUCTS_CREATE: 'products:create',
    PRODUCTS_UPDATE: 'products:update',
    PRODUCTS_DELETE: 'products:delete',

    // Reports
    REPORTS_VIEW: 'reports:view',
    REPORTS_EXPORT: 'reports:export',
    REPORTS_CREATE: 'reports:create',

    // Attendance
    ATTENDANCE_VIEW: 'attendance:view',
    ATTENDANCE_MANAGE: 'attendance:manage',
    ATTENDANCE_REPORTS: 'attendance:reports',

    // Files
    FILES_VIEW: 'files:view',
    FILES_UPLOAD: 'files:upload',
    FILES_DELETE: 'files:delete',

    // Automation
    AUTOMATION_VIEW: 'automation:view',
    AUTOMATION_CREATE: 'automation:create',
    AUTOMATION_UPDATE: 'automation:update',
    AUTOMATION_DELETE: 'automation:delete',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// Group permissions for UI display
export const PERMISSION_GROUPS = [
    {
        label: 'Dashboard',
        permissions: [PERMISSIONS.DASHBOARD_VIEW],
    },
    {
        label: 'Users & Roles',
        permissions: [
            PERMISSIONS.USERS_VIEW,
            PERMISSIONS.USERS_CREATE,
            PERMISSIONS.USERS_UPDATE,
            PERMISSIONS.USERS_DELETE,
            PERMISSIONS.ROLES_MANAGE,
        ],
    },
    {
        label: 'CRM (Leads)',
        permissions: [
            PERMISSIONS.LEADS_VIEW,
            PERMISSIONS.LEADS_CREATE,
            PERMISSIONS.LEADS_UPDATE,
            PERMISSIONS.LEADS_DELETE,
            PERMISSIONS.LEADS_CONVERT,
        ],
    },
    {
        label: 'CRM (Clients)',
        permissions: [
            PERMISSIONS.CLIENTS_VIEW,
            PERMISSIONS.CLIENTS_CREATE,
            PERMISSIONS.CLIENTS_UPDATE,
            PERMISSIONS.CLIENTS_DELETE,
        ],
    },
    {
        label: 'CRM (Contacts)',
        permissions: [
            PERMISSIONS.CONTACTS_VIEW,
            PERMISSIONS.CONTACTS_CREATE,
            PERMISSIONS.CONTACTS_UPDATE,
            PERMISSIONS.CONTACTS_DELETE,
        ],
    },
    {
        label: 'Sales (Quotations)',
        permissions: [
            PERMISSIONS.QUOTATIONS_VIEW,
            PERMISSIONS.QUOTATIONS_CREATE,
            PERMISSIONS.QUOTATIONS_UPDATE,
            PERMISSIONS.QUOTATIONS_DELETE,
            PERMISSIONS.QUOTATIONS_APPROVE,
            PERMISSIONS.QUOTATIONS_SEND,
            PERMISSIONS.QUOTATIONS_CONFIRM,
        ],
    },
    {
        label: 'Sales (Orders)',
        permissions: [
            PERMISSIONS.SALES_ORDERS_VIEW,
            PERMISSIONS.SALES_ORDERS_UPDATE,
            PERMISSIONS.SALES_ORDERS_DELETE,
            PERMISSIONS.SALES_ORDERS_INVOICE,
            PERMISSIONS.SALES_ORDERS_CANCEL,
        ],
    },
    {
        label: 'Jobs',
        permissions: [
            PERMISSIONS.JOBS_VIEW,
            PERMISSIONS.JOBS_CREATE,
            PERMISSIONS.JOBS_UPDATE,
            PERMISSIONS.JOBS_DELETE,
            PERMISSIONS.JOBS_ASSIGN,
            PERMISSIONS.JOBS_STATUS,
        ],
    },
    {
        label: 'Production',
        permissions: [
            PERMISSIONS.PRODUCTION_VIEW,
            PERMISSIONS.PRODUCTION_UPDATE,
            PERMISSIONS.PRODUCTION_MANAGE,
        ],
    },
    {
        label: 'Finance (Invoices)',
        permissions: [
            PERMISSIONS.INVOICES_VIEW,
            PERMISSIONS.INVOICES_CREATE,
            PERMISSIONS.INVOICES_UPDATE,
            PERMISSIONS.INVOICES_DELETE,
            PERMISSIONS.INVOICES_SEND,
        ],
    },
    {
        label: 'Finance (Payments)',
        permissions: [
            PERMISSIONS.PAYMENTS_VIEW,
            PERMISSIONS.PAYMENTS_CREATE,
            PERMISSIONS.PAYMENTS_UPDATE,
        ],
    },
    {
        label: 'Finance (Expenses)',
        permissions: [
            PERMISSIONS.EXPENSES_VIEW,
            PERMISSIONS.EXPENSES_CREATE,
            PERMISSIONS.EXPENSES_UPDATE,
            PERMISSIONS.EXPENSES_APPROVE,
        ],
    },
    {
        label: 'Purchases',
        permissions: [
            PERMISSIONS.PURCHASES_VIEW,
            PERMISSIONS.PURCHASES_CREATE,
            PERMISSIONS.PURCHASES_UPDATE,
            PERMISSIONS.PURCHASES_APPROVE,
        ],
    },
    {
        label: 'Vendors',
        permissions: [
            PERMISSIONS.VENDORS_VIEW,
            PERMISSIONS.VENDORS_CREATE,
            PERMISSIONS.VENDORS_UPDATE,
            PERMISSIONS.VENDORS_DELETE,
        ],
    },
    {
        label: 'Products',
        permissions: [
            PERMISSIONS.PRODUCTS_VIEW,
            PERMISSIONS.PRODUCTS_CREATE,
            PERMISSIONS.PRODUCTS_UPDATE,
            PERMISSIONS.PRODUCTS_DELETE,
        ],
    },
    {
        label: 'Reports',
        permissions: [
            PERMISSIONS.REPORTS_VIEW,
            PERMISSIONS.REPORTS_EXPORT,
            PERMISSIONS.REPORTS_CREATE,
        ],
    },
    {
        label: 'Attendance',
        permissions: [
            PERMISSIONS.ATTENDANCE_VIEW,
            PERMISSIONS.ATTENDANCE_MANAGE,
            PERMISSIONS.ATTENDANCE_REPORTS,
        ],
    },
    {
        label: 'Files',
        permissions: [
            PERMISSIONS.FILES_VIEW,
            PERMISSIONS.FILES_UPLOAD,
            PERMISSIONS.FILES_DELETE,
        ],
    },
    {
        label: 'Automation',
        permissions: [
            PERMISSIONS.AUTOMATION_VIEW,
            PERMISSIONS.AUTOMATION_CREATE,
            PERMISSIONS.AUTOMATION_UPDATE,
            PERMISSIONS.AUTOMATION_DELETE,
        ],
    },
    {
        label: 'Settings',
        permissions: [
            PERMISSIONS.SETTINGS_VIEW,
            PERMISSIONS.SETTINGS_UPDATE,
            PERMISSIONS.TENANT_SETTINGS,
        ],
    },
];
